import { sourceHash } from "./source-hash";

/**
 * A translation row is addressed by `(owner, element_key, slot, source_hash, locale)`.
 * Two identical strings are therefore two elements with two keys and two independent
 * translations, which is what lets one CTA read "Cómpralos" under the shoes and
 * "Cómprala" under the dress.
 *
 * Dependency-free apart from the browser-safe hash: the Liquid tag consuming these keys
 * is registered in the shared engine factory, which the browser preview also builds.
 */

/**
 * Which facet of an element a translation replaces.
 *
 * A slot is a facet of one element, not a kind of content. A new channel adds keys (a
 * subject is an element whose key is the field name). A new localizable attribute of an
 * existing element adds a slot. Several slots share one element key — a line, the URL
 * it links to, and the image it points at are one clickable thing.
 */
export const TEXT_SLOT = "text";
/** Where the element points: a URL, or the number a phone button dials. */
export const HREF_SLOT = "href";
export const SRC_SLOT = "src";
export const ALT_SLOT = "alt";

/** Declared from the constants so the union and the values cannot drift apart. */
export type TranslationSlot =
  | typeof TEXT_SLOT
  | typeof HREF_SLOT
  | typeof SRC_SLOT
  | typeof ALT_SLOT;

const SLOTS: TranslationSlot[] = [TEXT_SLOT, HREF_SLOT, SRC_SLOT, ALT_SLOT];

export const SUBJECT_CORE = "subject" as const;
export const PREHEADER_CORE = "preheader" as const;
/**
 * An SMS or push body translates as one element: its lines read as one message, not as layout.
 * One constant serves both channels, since two cores spelling "body" would collide in the context label record.
 */
export const BODY_CORE = "body" as const;
export const TITLE_CORE = "title" as const;
export const SUBTITLE_CORE = "subtitle" as const;
/** A push notification's image URL, translated in the `src` slot. */
export const IMAGE_CORE = "image" as const;
/** An SMS link translates as one element whose facet is the URL, so it sits at the href slot. */
export const SMS_LINK_CORE = "link" as const;
/** The dispatcher renders the SMS link under this key; the render stamps it as SMS_LINK_CORE. */
export const SMS_LINK_TEMPLATE_KEY = "_link" as const;

export type MetadataFieldCore =
  | typeof SUBJECT_CORE
  | typeof PREHEADER_CORE
  | typeof BODY_CORE
  | typeof TITLE_CORE
  | typeof SUBTITLE_CORE
  | typeof IMAGE_CORE
  | typeof SMS_LINK_CORE;

const METADATA_FIELD_CORES: ReadonlySet<string> = new Set<MetadataFieldCore>([
  SUBJECT_CORE,
  PREHEADER_CORE,
  BODY_CORE,
  TITLE_CORE,
  SUBTITLE_CORE,
  IMAGE_CORE,
  SMS_LINK_CORE,
]);

/**
 * The CSV sorts these into their own section, and the reclaim index excludes them: a
 * whole-field key is re-derived every save, so handing it to a body line fuses two
 * elements onto one key.
 */
export function isMetadataFieldCore(core: string): core is MetadataFieldCore {
  return METADATA_FIELD_CORES.has(core);
}

export function asTranslationSlot(value: string): TranslationSlot {
  const slot = SLOTS.find((candidate) => candidate === value);
  if (slot === undefined) {
    throw new Error(
      `This cell type ("${value}") isn't supported. Reload the tab and try again.`,
    );
  }
  return slot;
}

export function isTranslationSlot(value: string): value is TranslationSlot {
  return SLOTS.some((candidate) => candidate === value);
}

/** A single translatable unit: one CSV row, one `cep_translations` row per locale. */
export type LocalizableElement = {
  elementKey: string;
  slot: TranslationSlot;
  /** Human context shown in the CSV, e.g. "Paragraph 2". Cosmetic — never a key. */
  context: string;
  sourceText: string;
  sourceHash: string;
  /**
   * Read from the tag rather than from a settings row, so each content version
   * self-describes its own selection. A draft's deselection therefore cannot mute a
   * translation in a campaign that is still draining.
   */
  off: boolean;
  /** The arm content row's `variant_id`; null or absent for the base body. */
  variantId?: string | null;
};

/**
 * The identity of one translatable row, flattened for the CSV, upload errors, the
 * `data-ht-key` a canvas click resolves, and the UI's in-memory maps. All of those must
 * spell a row the same way, since a marketer reads an id in one place and types it in
 * another.
 *
 * Element first so a spreadsheet sorted by id clusters an element's `text`, `href`,
 * `src` and `alt` rows adjacently. Colon separator because keys own dots and never contain a
 * colon, so one split yields exactly (key, slot). Always slot-qualified, including
 * `text`, or a button's text and its href would share an id.
 *
 * Takes an object rather than two positional strings: both halves are strings, so
 * transposing them yields a plausible-looking id.
 */
export function translationRowId(element: {
  elementKey: string;
  slot: TranslationSlot;
}): string {
  return `${element.elementKey}:${element.slot}`;
}

export function localizableElement(args: {
  elementKey: string;
  slot: TranslationSlot;
  context: string;
  sourceText: string;
  off?: boolean;
}): LocalizableElement {
  return {
    elementKey: args.elementKey,
    slot: args.slot,
    context: args.context,
    sourceText: args.sourceText,
    sourceHash: sourceHash(args.sourceText),
    off: args.off === true,
  };
}
